import type { Level, Opening, OpeningDesign, OpeningKind, Wall } from '../core/hp/types';
import { validateLevel, type EditResult } from '../core/geometry/edit';
import { syncOpeningTwins } from '../core/geometry/openingTwins';
import { carryCurtains } from './curtains';
import { t } from '../i18n/I18n';

/**
 * How the width of an opening sticks while one of its Points is dragged (the Figma rules, card 12): to
 * whole 10 cm within 7 screen pixels, and more strongly, within 10, to whole 50 cm — the 100 and 140 cm
 * where a door's leaves change are among them.
 */
export const OPENING_WIDTH_SNAP = { step: .1, radius: 7, strongStep: .5, strongRadius: 10 };

/** `width` stuck to the nearest whole step within reach at `scale` metres per screen pixel, else as it is. */
export function snapOpeningWidth(width: number, scale: number): number {
  const { step, radius, strongStep, strongRadius } = OPENING_WIDTH_SNAP;
  for (const [size, reach] of [[step, radius], [strongStep, strongRadius]]) {
    const whole = Math.round(width / size) * size;
    if (Math.abs(whole - width) <= reach * scale) return Math.round(whole * 1e4) / 1e4;
  }
  return width;
}

/**
 * The opening `id` with the end at `side` moved `delta` metres along its wall and the other end kept,
 * held clear of the wall's ends and of the openings beside it. With `snap`, the width first sticks as
 * `snapOpeningWidth` makes it at that scale; the limits apply after it.
 */
export function previewOpeningResize(base: Level, id: string, side: -1 | 1, delta: number, snap?: { scale: number }): EditResult {
  const opening = base.openings.find(value => value.id === id), wall = base.walls.find(value => value.id === opening?.wallId);
  if (!opening || !wall) return { error: t('#GeometryOpeningNotFound') };
  if (!Number.isFinite(delta)) return { error: t('#OpeningPropsCheckWidth') };
  if (Math.abs(delta) < .00005) return { level: base };
  const start = opening.offset - opening.width / 2, end = opening.offset + opening.width / 2;
  let lower = .02, upper = wall.length - .02;
  for (const other of base.openings) {
    const overlap = Math.min(opening.sill + opening.height, other.sill + other.height) - Math.max(opening.sill, other.sill);
    if (other.id === id || other.wallId !== wall.id || overlap <= .001) continue;
    if (other.offset < opening.offset) lower = Math.max(lower, other.offset + other.width / 2 + .02);
    else upper = Math.min(upper, other.offset - other.width / 2 - .02);
  }
  const maxWidth = wall.length - .04;
  const min = side === -1 ? Math.max(lower, end - maxWidth) : start + .01;
  const max = side === -1 ? end - .01 : Math.min(upper, start + maxWidth);
  if (min > max) return { error: t('#OpeningPropsNoRoomToResize') };
  let target = (side === -1 ? start : end) + Math.round(delta * 1e4) / 1e4;
  if (snap) { const width = snapOpeningWidth(side === -1 ? end - target : target - start, snap.scale); target = side === -1 ? end - width : start + width; }
  const moved = Math.max(min, Math.min(max, target));
  const a = side === -1 ? moved : start, b = side === 1 ? moved : end;
  if (Math.abs(a - start) < 1e-9 && Math.abs(b - end) < 1e-9) return { level: base };
  const updated = { ...opening, width: b - a, offset: (a + b) / 2 };
  // A door from the app has a copy on the other room's wall, and it keeps up; its curtains stay centred on it.
  const next = carryCurtains(base, syncOpeningTwins(base, { ...base, openings: base.openings.map(value => value === opening ? updated : value) }, [id]));
  const error = validateLevel(next, base);
  return error ? { error } : { level: next };
}

export function openingKind(opening: Opening, tags: readonly string[] = []): OpeningKind | 'opening' {
  if (opening.kind) return opening.kind;
  const id = opening.modelRef.id.replace(/^Models\//i, '').toLowerCase();
  const caption = (opening.caption ?? '').trim().toLowerCase();
  const semanticTags = tags.map(value => value.toLowerCase());
  // i18n-ignore-start: captions stored in projects, compared as saved
  if (semanticTags.includes('arch') || ['arch', 'арка'].includes(caption) || id === 'wall.doors/602/602') return 'arch';
  if (semanticTags.includes('window')) return 'window';
  if (semanticTags.includes('door')) return 'door';
  if (id.startsWith('wall.windows/')) return 'window';
  if (id.startsWith('wall.doors/')) return 'door';
  if (id.startsWith('wall.arches/')) return 'arch';
  if (['door', 'дверь'].includes(caption)) return 'door';
  if (['window', 'окно'].includes(caption)) return 'window';
  // i18n-ignore-end
  return 'opening';
}

/**
 * A door or window whose catalog model has parts to dress, as a piece of furniture's (the app's FurnitureColors): not
 * the editor's own drawn one, which has no catalog model, and not an empty gap.
 */
export function paintableOpening(opening: Opening, tags: readonly string[]): boolean {
  return opening.modelRef.kind !== 'none' && !!opening.modelRef.id && !opening.design?.style && !tags.some(tag => tag.toLowerCase() === 'gap');
}

export function openingDesign(opening: Opening, wallThickness = .2): Required<Omit<OpeningDesign, 'style'>> & Pick<OpeningDesign, 'style'> {
  return { style: opening.design?.style, openAngle: opening.design?.openAngle ?? 0,
    recess: opening.design?.recess ?? wallThickness / 2, curtains: opening.design?.curtains ?? 'none' };
}

export function validateOpeningDesign(opening: Opening, wall: Wall): string | null {
  const kind = openingKind(opening), design = openingDesign(opening, wall.thickness);
  if (!Number.isFinite(design.recess) || design.recess < 0 || design.recess > wall.thickness) return t('#OpeningPropsRecessRange');
  const maxAngle = kind === 'door' ? 120 : kind === 'window' ? 90 : 0;
  if (!Number.isFinite(design.openAngle) || design.openAngle < 0 || design.openAngle > maxAngle) return t('#OpeningPropsOpenAngleRange', maxAngle);
  if (design.openAngle && !design.style) return t('#OpeningPropsChooseSashForOpening');
  const styles = kind === 'door' ? ['solid', 'glazed', 'double-door'] : kind === 'window' ? ['single', 'double', 'triple'] : [];
  if (design.style && !styles.includes(design.style)) return t('#OpeningPropsSashDoesNotFit');
  if (!['none', 'sheer', 'drapes', 'roller'].includes(design.curtains) || (design.curtains !== 'none' && kind !== 'window')) return t('#OpeningPropsCurtainsWindowsOnly');
  return null;
}
